import React from "react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

interface EditableSelectCellProps {
  value: string;
  options: SelectOption[];
  hasError: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const EditableSelectCell: React.FC<EditableSelectCellProps> = ({
  value,
  options,
  hasError,
  onChange,
  placeholder = "Select…",
}) => {
  const handleChange = (e: SelectChangeEvent) => onChange(e.target.value);

  // Group options by their `group` field
  const groups = options.reduce<Record<string, SelectOption[]>>((acc, opt) => {
    const key = opt.group ?? "";
    return { ...acc, [key]: [...(acc[key] ?? []), opt] };
  }, {});

  const items: React.ReactNode[] = [];
  Object.entries(groups).forEach(([groupName, groupOptions]) => {
    if (groupName) {
      items.push(
        <MenuItem key={`header-${groupName}`} disabled sx={{ fontWeight: 700, fontSize: 11, opacity: 1 }}>
          {groupName}
        </MenuItem>,
      );
    }
    groupOptions.forEach((opt) =>
      items.push(
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>,
      ),
    );
  });

  return (
    <Box sx={{ width: "100%", px: 0.5 }}>
      <Select
        size="small"
        fullWidth
        displayEmpty
        value={value}
        onChange={handleChange}
        error={hasError}
        renderValue={(v) => (v ? options.find((o) => o.value === v)?.label ?? v : placeholder)}
        sx={{
          "& .MuiSelect-select": { fontSize: "0.875rem" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: hasError ? "error.main" : undefined,
          },
          backgroundColor: hasError ? "error.50" : undefined,
        }}
      >
        {items}
      </Select>
    </Box>
  );
};

export default EditableSelectCell;
